<template>
  <div class="net-toolbar" ref="el">
    <menu
      class="net-toolbar__main"
      ref="mainEl"
      @contextmenu.stop.prevent 
      @pointerdown.prevent=""
    >
      <button
        type="button"
        class="net-toolbar__scrollbutton -top"
        v-show="scrollButtonTopVisible"
        @click="scrollUp()"
      >
        <div>
          <lucide-icon name="chevron-up" />
        </div>
      </button>

      <net-toolbar-button
        tool-key="pointer"
        @mouseenter="setHoveredTool('pointer', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="mouse-pointer-2" />
      </net-toolbar-button>
      <net-toolbar-button
        tool-key="pan"
        @mouseenter="setHoveredTool('pan', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="hand" />
      </net-toolbar-button>
      <div class="net-toolbar__seperator"></div>
      <net-toolbar-button
        tool-key="target"
        v-if="netTools.availableTools?.target"
        @mouseenter="setHoveredTool('target', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="target" />
      </net-toolbar-button>
      <net-toolbar-button
        tool-key="resource"
        v-if="netTools.availableTools?.resource"
        @mouseenter="setHoveredTool('resource', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="resource" />
      </net-toolbar-button>
      <net-toolbar-button
        tool-key="index"
        v-if="netTools.availableTools?.index"
        @mouseenter="setHoveredTool('index', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="index" />
      </net-toolbar-button>
      <div class="net-toolbar__seperator" v-if="features.design"></div>
      <net-toolbar-button
        tool-key="shape"
        v-if="netTools.availableTools?.shape"
        :expandable="true"
        @mouseenter="setHoveredTool('shape', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @contextmenu.prevent="openSceneShapeContextMenu"
        v-on-long-press="[openSceneShapeLongPressMenu, { delay: 200 }]"
        @click="centerTool($event)"
      >
        <lucide-icon v-if="sceneShapeStore.defaults.shapeType == 'rectangle'" name="square" />
        <lucide-icon v-else-if="sceneShapeStore.defaults.shapeType == 'roundedRectangle'" name="rounded-rectangle" />
        <lucide-icon v-else-if="sceneShapeStore.defaults.shapeType == 'circle'" name="circle" />
        <lucide-icon v-else-if="sceneShapeStore.defaults.shapeType == 'triangle'" name="triangle" />
        <lucide-icon v-else-if="sceneShapeStore.defaults.shapeType == 'polygon'" name="pentagon" />

        <context-menu
          v-if="sceneShapeContextMenu"
          :x="sceneShapeContextMenu.x"
          :y="sceneShapeContextMenu.y"
          @close="sceneShapeContextMenu = null"
        >
          <button type="button" class="context-menu__entry" @click="setShapeType('rectangle')">
            <lucide-icon name="square" />
            {{ $t("shapes.rectangle") }}
          </button>
          <button type="button" class="context-menu__entry" @click="setShapeType('roundedRectangle')">
            <lucide-icon name="rounded-rectangle" />
            {{ $t("shapes.roundedRectangle") }}
          </button>
          <button type="button" class="context-menu__entry" @click="setShapeType('circle')">
            <lucide-icon name="circle" />
            {{ $t("shapes.circle") }}
          </button>
          <button type="button" class="context-menu__entry" @click="setShapeType('triangle')">
            <lucide-icon name="triangle" />
            {{ $t("shapes.triangle") }}
          </button>
        </context-menu>
      </net-toolbar-button>
      <net-toolbar-button
        tool-key="medium"
        v-if="netTools.availableTools?.medium"
        @mouseenter="setHoveredTool('medium', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="image" />
      </net-toolbar-button>
      <net-toolbar-button
        tool-key="text"
        v-if="netTools.availableTools?.text"
        @mouseenter="setHoveredTool('text', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="type" />
      </net-toolbar-button>
      <net-toolbar-button
        tool-key="chart"
        v-if="netTools.availableTools?.chart"
        @mouseenter="setHoveredTool('chart', $event)"
        @mouseleave="hoveredTool = null"
        @select="emit('update:tool', $event)"
        @click="centerTool($event)"
      >
        <lucide-icon name="chart-line" />
      </net-toolbar-button>

      <button
        type="button"
        class="net-toolbar__scrollbutton -bottom"
        v-show="scrollButtonBottomVisible"
        @click="scrollDown()"
      >
        <div>
          <lucide-icon name="chevron-down" />
        </div>
      </button>
    </menu>

    <transition name="net-toolbar__tooltip-transition">
      <div
        v-if="hoveredTool && !sceneShapeContextMenu"
        class="net-toolbar__tooltip"
        :style="{ top: hoverYPosition + 'px' }"
      >
        {{ $t(`netTools.${hoveredTool}`) }} 
        <kbd v-if="netTools.availableTools?.[hoveredTool].hotkey" class="net-toolbar__tooltip-hotkey">
          {{ netTools.availableTools[hoveredTool].hotkey }}
        </kbd>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { vOnLongPress } from '@vueuse/components';
import { useNetTools } from "@editor/net/NetTools";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSceneShapeStore } from "@editor/items/sceneShape/sceneShapes.store";

import NetToolbarButton from "./NetToolbarButton.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import { useElementBounding, useScroll } from "@vueuse/core";
import { useFeatures } from "@editor/store/features.store";
import type { NetToolKey } from "@editor/net/NetTools";
import type { Handleable } from "@core/types/Events";

const emit = defineEmits<{
  (e: "update:tool", value: NetToolKey): void;
}>();

const netTools = useNetTools();
const selection = useSelectionStore();
const features = useFeatures();

const el = useTemplateRef("el");
const mainEl = useTemplateRef("mainEl");

const hoveredTool = ref<NetToolKey | null>(null);
const hoverYPosition = ref(0);
function setHoveredTool(tool: NetToolKey, event: MouseEvent) {
  const button = (event.target as HTMLElement).closest("button");
  if (button == null) return;

  hoveredTool.value = tool;
  const buttonRect = button.getBoundingClientRect();
  hoverYPosition.value = buttonRect.top + buttonRect.height / 2 - el.value!.getBoundingClientRect().top;
}

// ===========================
// Overflow logic

const boundingBox = useElementBounding(mainEl);
const { arrivedState, y: scrollY, measure } = useScroll(mainEl, {
  behavior: "smooth",
});

const scrollButtonTopVisible = computed(() => {
  return !arrivedState.top;
});
const scrollButtonBottomVisible = ref(false);

function scrollDown(mouseEvent?: MouseEvent & Handleable) {
  if (mouseEvent) {
    mouseEvent.hasBeenHandled = true;
    mouseEvent.stopPropagation();
    mouseEvent.preventDefault();
  }
  const clientHeight = mainEl.value!.clientHeight;
  const scrollHeight = mainEl.value!.scrollHeight;

  // Scroll down by the height of the toolbar
  const newScrollTop = scrollY.value + clientHeight - 55;
  scrollY.value += newScrollTop;

  const bottom = Math.abs(newScrollTop)
      + clientHeight >= scrollHeight
      - 1; // -1 to account for rounding errors
  scrollButtonBottomVisible.value = !bottom;
}

function scrollUp(mouseEvent?: MouseEvent & Handleable) {
  if (mouseEvent) {
    mouseEvent.hasBeenHandled = true;
    mouseEvent.stopPropagation();
    mouseEvent.preventDefault();
  }

  const clientHeight = mainEl.value!.clientHeight;

  // Scroll up by the height of the toolbar
  scrollY.value -= clientHeight - 55;
}

watch(boundingBox.height, () => {
  // Re-measure the scroll position when the height of the toolbar changes
  measure();
});
watch(() => arrivedState.bottom, () => {
  // Update the visibility of the scroll button when the arrived state changes
  scrollButtonBottomVisible.value = !arrivedState.bottom;
})

function centerTool(event: MouseEvent) {
  // scroll the toolbar to center on the clicked tool
  const button = (event.target as HTMLElement).closest("button");
  if (button == null) return;

  const scrollTop = mainEl.value!.scrollTop;
  const scrollCenter = scrollTop + mainEl.value!.clientHeight / 2;
  const buttonTop = button.offsetTop;
  const buttonHeight = button.clientHeight;
  const buttonCenter = buttonTop + buttonHeight / 2;

  const scrollOffset = buttonCenter - scrollCenter;
  if (Math.abs(scrollOffset) < 80) {
    // If the button is already close to the center, do not scroll
    return;
  }
  mainEl.value!.scrollTo({
    top: scrollTop + scrollOffset / 2,
    behavior: "smooth"
  });
}

// ===========================
// SceneShapes

const sceneShapeStore = useSceneShapeStore();

const sceneShapeContextMenu = ref<{
  x: number;
  y: number;
} | null>(null);

function openSceneShapeContextMenu(event: PointerEvent) {
  event.preventDefault();
  event.stopPropagation();

  const button = (event.target as HTMLElement).closest("button");
  if (button == null) return;

  const buttonRect = button.getBoundingClientRect();

  sceneShapeContextMenu.value = {
    x: buttonRect.left + buttonRect.width + 10,
    y: buttonRect.top
  };
}

function openSceneShapeLongPressMenu(event: PointerEvent) {
  openSceneShapeContextMenu(event);
}

function setShapeType(type: "rectangle" | "roundedRectangle" | "circle" | "triangle") {
  sceneShapeStore.defaults.shapeType = type;
  sceneShapeContextMenu.value = null;
  setTool("shape");
}

function setTool(tool: NetToolKey) {
  netTools.setTool(tool);
  if (netTools.tool.type == "creating") {
    // deselect all items when switching to a creating tool
    selection.clear();
  }
  emit("update:tool", tool);
}
</script>

<style lang="scss">
.net-toolbar {
  position: absolute;
  left: 1rem;
  top: 1rem;
  bottom: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 3rem;
  padding-bottom: 2rem;

  --hover-bg-color: rgba(var(--tint-contrast), 0.02);
  --active-bg-color: hsl(var(--color-primary-h), 80%, 94%);
  --active-bg-color-hover: hsl(var(--color-primary-h), 80%, 90%);

  .dark & {
    --active-bg-color: hsl(var(--color-primary-h), 80%, 17%);
    --active-bg-color-hover: hsl(var(--color-primary-h), 80%, 20%);
  }

  &__main {
    position: relative;
    padding: 0;
    border-radius: 0.25rem;
    margin: 0;
    width: max-content;
    min-width: min-content;
    min-height: 0;
    overflow: hidden;

    list-style: none;
    display: flex;
    flex-direction: column;

    background-color: var(--color-background-close);
    box-shadow: 0 0.05em 0.25em var(--color-shadow-2);
    pointer-events: all;
  }

  &__seperator {
    width: 100%;
    flex-shrink: 0;
    padding: 0.5rem 0;
    display: flex;
    justify-content: center;
    &::before {
      content: "";
      display: block;
      width: 60%;
      height: 1px;
      background-color: var(--color-border);
    }
  }

  &__sidebar-toggle {
    position: absolute;
    top: 0;
    left: 0;
    appearance: none;
    background-color: transparent;
    border: none;
    padding: 0.5rem;
    margin-bottom: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: var(--color-text);
  }

  &__tooltip {
    position: absolute;
    left: calc(100% + 0.75rem);
    height: 2.5rem;
    margin-top: -1.25rem;
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background-color: var(--active-bg-color);
    border-radius: 0.25rem;
    font-size: 14px;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: -1rem;
      width: 0;
      height: 0;
      border: 0.5rem solid transparent;
      border-right-color: var(--active-bg-color);
      transform: translateY(-50%);
    }

    &-hotkey {
      margin-left: 0.5rem;
      font-size: 0.75rem;
      text-transform: uppercase;
      background-color: rgba(var(--tint-contrast), 0.1);
      width: 1.5rem;
      text-align: center;
      height: 1.5rem;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      // box-shadow: inset 0 -2px 3px 0px rgba(var(--tint-contrast), 0.2);
    }

    &-transition-enter-active,
    &-transition-leave-active {
      transition: opacity 500ms;
    }
    &-transition-enter-active {
      transition-delay: 100ms;
    }

    &-transition-enter-to,
    &-transition-leave-from {
      opacity: 1;
    }

    &-transition-enter-from,
    &-transition-leave-to {
      opacity: 0;
    }
  }

  &__scrollbutton {
    appearance: none;
    border: none;
    margin-bottom: auto;
    height: 0;
    overflow: visible;
    font-size: 1.5rem;
    color: var(--color-text);
    cursor: pointer;
    position: sticky;
    width: 100%;
    z-index: 1;

    div {
      position: absolute;
      left: 0;
      padding: 0.5rem;
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 100%;
      background-color: var(--color-background-close);
      box-shadow: 0 0 0.4em hsla(0, 0%, 0%, 0.1);
    }
    
    &.-bottom {
      bottom: 0;
      div {
        bottom: 0;
      }
    }

    &.-top {
      top: 0;
      div {
        top: 0;
      }
    }

    &:hover {
      background-color: var(--hover-bg-color);
    }
  }
}
</style>
